import { useState, useEffect, useMemo, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { journalAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, PenLine, Sparkles, Battery, Loader2, TrendingUp, BookOpen, Eye, EyeOff, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

// Emotion Wheel categories
const EMOTION_WHEEL = {
  comfortable: {
    label: "Comfortable",
    emotions: [
      "Content", "Peaceful", "Relaxed", "Serene", "Satisfied", "Secure",
      "Hopeful", "Empowered", "Proud", "Confident", "Grateful", "Joyful"
    ]
  },
  uncomfortable: {
    label: "Uncomfortable",
    emotions: [
      "Sad", "Disappointed", "Lonely", "Hurt", "Guilty", "Ashamed",
      "Anxious", "Worried", "Scared", "Frustrated", "Overwhelmed", "Angry"
    ]
  }
};

// Smart prompts based on mood
const MOOD_PROMPTS = {
  "Content": "What moments today contributed to this feeling of contentment?",
  "Peaceful": "What's helping you feel at peace right now?",
  "Relaxed": "What allowed you to slow down and relax today?",
  "Serene": "Describe the calm you're experiencing. What's grounding you?",
  "Satisfied": "What accomplishment or moment left you feeling satisfied?",
  "Secure": "What's giving you a sense of safety and stability right now?",
  "Hopeful": "What's sparking hope for you? What possibilities are you seeing?",
  "Empowered": "What helped you feel capable and in control today?",
  "Proud": "What are you proud of yourself for? Celebrate it here.",
  "Confident": "What's fueling your confidence right now?",
  "Grateful": "What small moment brought you joy today?",
  "Joyful": "What's bringing you joy? Let yourself savor it.",
  "Sad": "What's weighing on your heart right now?",
  "Disappointed": "What didn't go as you hoped? Give yourself space to process.",
  "Lonely": "When did you last feel truly connected? What do you need right now?",
  "Hurt": "What happened that caused this hurt? You're safe to express it here.",
  "Guilty": "What's the guilt about? Sometimes writing it out brings perspective.",
  "Ashamed": "Shame is heavy. What would you tell a friend feeling this way?",
  "Anxious": "What's the one thing weighing on you most right now?",
  "Worried": "What's on your mind? Sometimes naming the worry helps shrink it.",
  "Scared": "What fear is showing up for you? You're not alone in this.",
  "Frustrated": "What's blocking you right now? Let it out.",
  "Overwhelmed": "What's the one thing weighing on you most right now?",
  "Angry": "What triggered this feeling? Give yourself permission to express it."
};

// Check if mood is comfortable
const isComfortableMood = (mood) => {
  return EMOTION_WHEEL.comfortable.emotions.map(e => e.toLowerCase()).includes(mood?.toLowerCase());
};

// Get emoji for mood category
const getMoodEmoji = (mood) => {
  if (isComfortableMood(mood)) return "✨";
  return "💭";
};

// Get energy label
const getEnergyLabel = (level) => {
  if (level <= 2) return "Very Low";
  if (level <= 4) return "Low";
  if (level <= 6) return "Moderate";
  if (level <= 8) return "High";
  return "Very High";
};

// Mood to numeric score for charting (comfortable = positive, uncomfortable = shows as different color)
const getMoodScore = (mood) => {
  if (!mood) return null;
  return isComfortableMood(mood) ? 1 : -1;
};

const CHART_TOOLTIP_STYLE = {
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const CHART_TICK_STYLE = { fontSize: 12 };

const ENERGY_DOT_STYLE = { fill: '#2563eb', strokeWidth: 0, r: 4 };

export default function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detectingMood, setDetectingMood] = useState(false);
  const [suggestedMoods, setSuggestedMoods] = useState(null);
  const [activeTab, setActiveTab] = useState("entries");
  const [expandedEntries, setExpandedEntries] = useState({}); // Track which entries are expanded
  const [entryData, setEntryData] = useState({ 
    title: '', 
    content: '', 
    mood: '', 
    energy_level: null 
  });

  const toggleEntryExpanded = (entryId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };

  const loadEntries = useCallback(async () => {
    try {
      const response = await journalAPI.getAll();
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Prepare trend data for charts
  const trendData = useMemo(() => {
    const entriesWithData = entries.filter(e => e.mood || e.energy_level);
    
    // Group by date and get last 14 entries
    const recentEntries = entriesWithData.slice(0, 14).reverse();
    
    return recentEntries.map(entry => ({
      date: new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      energy: entry.energy_level || null,
      mood: entry.mood,
      moodType: isComfortableMood(entry.mood) ? 'comfortable' : 'uncomfortable',
      moodScore: getMoodScore(entry.mood)
    }));
  }, [entries]);

  // Pre-filtered trend data to avoid recomputing in JSX
  const energyTrendData = useMemo(() => trendData.filter(d => d.energy), [trendData]);
  const moodTrendData = useMemo(() => trendData.filter(d => d.mood), [trendData]);

  // Mood frequency data for bar chart
  const moodFrequency = useMemo(() => {
    const frequency = {};
    entries.forEach(entry => {
      if (entry.mood) {
        frequency[entry.mood] = (frequency[entry.mood] || 0) + 1;
      }
    });
    
    return Object.entries(frequency)
      .map(([mood, count]) => ({
        mood,
        count,
        fill: isComfortableMood(mood) ? '#10b981' : '#f59e0b'
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [entries]);

  // Stats summary
  const stats = useMemo(() => {
    const entriesWithEnergy = entries.filter(e => e.energy_level);
    const avgEnergy = entriesWithEnergy.length > 0 
      ? (entriesWithEnergy.reduce((sum, e) => sum + e.energy_level, 0) / entriesWithEnergy.length).toFixed(1)
      : null;
    
    const entriesWithMood = entries.filter(e => e.mood);
    const comfortableCount = entriesWithMood.filter(e => isComfortableMood(e.mood)).length;
    const comfortablePercent = entriesWithMood.length > 0 
      ? Math.round((comfortableCount / entriesWithMood.length) * 100)
      : null;
    
    const topMood = moodFrequency[0]?.mood || null;
    
    return { avgEnergy, comfortablePercent, topMood, totalEntries: entries.length };
  }, [entries, moodFrequency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = { ...entryData };
      if (!dataToSubmit.energy_level) delete dataToSubmit.energy_level;
      if (!dataToSubmit.mood) delete dataToSubmit.mood;
      
      await journalAPI.create(dataToSubmit);
      toast.success('Journal entry created!');
      setDialogOpen(false);
      setEntryData({ title: '', content: '', mood: '', energy_level: null });
      setSuggestedMoods(null);
      loadEntries();
    } catch (error) {
      toast.error('Failed to create entry');
    }
  };

  const handleDetectMood = async () => {
    if (!entryData.content || entryData.content.length < 20) {
      toast.error('Please write at least a few sentences for mood detection');
      return;
    }
    
    setDetectingMood(true);
    try {
      const response = await journalAPI.detectMood(entryData.content);
      setSuggestedMoods(response.data);
      toast.success('Mood analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze mood');
    } finally {
      setDetectingMood(false);
    }
  };

  const selectSuggestedMood = (mood) => {
    setEntryData({ ...entryData, mood });
    toast.success(`Selected: ${mood}`);
  };

  const resetForm = () => {
    setEntryData({ title: '', content: '', mood: '', energy_level: null });
    setSuggestedMoods(null);
  };

  const currentPrompt = entryData.mood ? MOOD_PROMPTS[entryData.mood] : null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="journal-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Journal</h1>
            <p className="text-muted-foreground">Reflect on your thoughts and track your emotional journey</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-entry-btn">
                <Plus size={16} className="mr-2" />
                New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">New Journal Entry</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="journal-form">
                {/* Title */}
                <div>
                  <Label htmlFor="entry-title">Title</Label>
                  <Input
                    id="entry-title"
                    data-testid="entry-title-input"
                    value={entryData.title}
                    onChange={(e) => setEntryData({ ...entryData, title: e.target.value })}
                    required
                    placeholder="Give your entry a title..."
                    className="mt-2 rounded-xl"
                  />
                </div>

                {/* Mood Selection with Emotion Wheel */}
                <div>
                  <Label htmlFor="entry-mood">How are you feeling? (Optional)</Label>
                  <Select 
                    value={entryData.mood} 
                    onValueChange={(value) => setEntryData({ ...entryData, mood: value })}
                  >
                    <SelectTrigger className="mt-2 rounded-xl" data-testid="mood-select">
                      <SelectValue placeholder="Select from the Emotion Wheel" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                          ✨ Comfortable
                        </SelectLabel>
                        {EMOTION_WHEEL.comfortable.emotions.map((emotion) => (
                          <SelectItem key={emotion} value={emotion} className="pl-4">
                            {emotion}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-amber-600 flex items-center gap-1 mt-2">
                          💭 Uncomfortable
                        </SelectLabel>
                        {EMOTION_WHEEL.uncomfortable.emotions.map((emotion) => (
                          <SelectItem key={emotion} value={emotion} className="pl-4">
                            {emotion}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {/* Smart Prompt based on mood */}
                {currentPrompt && (
                  <div className="bg-muted/50 p-3 rounded-xl border border-border/50">
                    <p className="text-sm text-muted-foreground italic">
                      <span className="font-medium text-foreground">Prompt:</span> {currentPrompt}
                    </p>
                  </div>
                )}

                {/* Energy Level Slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Battery size={16} />
                      Energy Level (Optional)
                    </Label>
                    {entryData.energy_level && (
                      <span className="text-sm text-muted-foreground">
                        {entryData.energy_level}/10 - {getEnergyLabel(entryData.energy_level)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">Low</span>
                    <Slider
                      data-testid="energy-slider"
                      value={entryData.energy_level ? [entryData.energy_level] : [5]}
                      onValueChange={(value) => setEntryData({ ...entryData, energy_level: value[0] })}
                      min={1}
                      max={10}
                      step={1}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground">High</span>
                  </div>
                  {entryData.energy_level && (
                    <button
                      type="button"
                      onClick={() => setEntryData({ ...entryData, energy_level: null })}
                      className="text-xs text-muted-foreground hover:text-foreground mt-1"
                    >
                      Clear energy level
                    </button>
                  )}
                </div>

                {/* Journal Content */}
                <div>
                  <Label htmlFor="entry-content">Your Thoughts</Label>
                  <Textarea
                    id="entry-content"
                    data-testid="entry-content-input"
                    value={entryData.content}
                    onChange={(e) => setEntryData({ ...entryData, content: e.target.value })}
                    required
                    placeholder="What's on your mind? Write freely..."
                    className="mt-2 rounded-xl"
                    rows={6}
                  />
                </div>

                {/* AI Mood Detection */}
                <div className="border-t border-border/50 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDetectMood}
                    disabled={detectingMood || !entryData.content}
                    className="w-full rounded-full"
                    data-testid="detect-mood-btn"
                  >
                    {detectingMood ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Analyzing your entry...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} className="mr-2" />
                        Help me identify my mood
                      </>
                    )}
                  </Button>

                  {/* Suggested Moods - No commentary, just mood buttons */}
                  {suggestedMoods && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-xl border border-border/50" data-testid="mood-suggestions">
                      <p className="text-sm font-medium mb-3">Based on your entry, you might be feeling:</p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedMoods.suggested_moods.map((mood) => (
                          <Button
                            key={mood}
                            type="button"
                            variant={entryData.mood === mood ? "default" : "outline"}
                            size="sm"
                            onClick={() => selectSuggestedMood(mood)}
                            className="rounded-full"
                            data-testid={`suggested-mood-${mood.toLowerCase()}`}
                          >
                            {getMoodEmoji(mood)} {mood}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full rounded-full" data-testid="entry-submit-btn">
                  Save Entry
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for Entries and Trends */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 rounded-full p-1 bg-muted">
            <TabsTrigger value="entries" className="rounded-full data-[state=active]:bg-background" data-testid="entries-tab">
              <BookOpen size={16} className="mr-2" />
              Entries
            </TabsTrigger>
            <TabsTrigger value="trends" className="rounded-full data-[state=active]:bg-background" data-testid="trends-tab">
              <TrendingUp size={16} className="mr-2" />
              Trends
            </TabsTrigger>
          </TabsList>

          {/* Entries Tab */}
          <TabsContent value="entries" className="mt-6">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading entries...</div>
            ) : entries.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl" data-testid="no-entries-message">
                <PenLine className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">Start Your Journal</h3>
                <p className="text-muted-foreground">
                  Write your first entry to begin tracking your thoughts and emotions
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {entries.map((entry, index) => {
                  const isExpanded = expandedEntries[entry.id];
                  return (
                    <Card key={entry.id} className="rounded-2xl border-border overflow-hidden" data-testid={`entry-${index}`}>
                      {/* Entry Header - Always visible */}
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-serif truncate">{entry.title}</h3>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                              <Calendar size={12} />
                              <span>
                                {new Date(entry.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {entry.energy_level && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                                <Battery size={12} />
                                {entry.energy_level}/10
                              </span>
                            )}
                            {entry.mood && (
                              <span className={`text-xs px-3 py-1 rounded-full ${
                                isComfortableMood(entry.mood) 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {getMoodEmoji(entry.mood)} {entry.mood}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Click to view/hide button */}
                        <button
                          onClick={() => toggleEntryExpanded(entry.id)}
                          className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                          data-testid={`toggle-entry-${index}`}
                        >
                          {isExpanded ? (
                            <>
                              <EyeOff size={14} />
                              <span>Hide entry</span>
                            </>
                          ) : (
                            <>
                              <Eye size={14} />
                              <span>View entry</span>
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Entry Content - Hidden by default for privacy */}
                      {isExpanded && (
                        <div className="px-5 pb-5 pt-0 border-t border-border/50">
                          <p className="text-muted-foreground whitespace-pre-wrap pt-4 text-sm leading-relaxed">
                            {entry.content}
                          </p>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="mt-6">
            {entries.length < 2 ? (
              <Card className="p-12 text-center rounded-2xl" data-testid="no-trends-message">
                <TrendingUp className="mx-auto mb-4 opacity-50 text-muted-foreground" size={48} />
                <h3 className="text-xl font-serif mb-2">Not Enough Data Yet</h3>
                <p className="text-muted-foreground">
                  Add a few more journal entries with moods and energy levels to see your trends
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Stats Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4 rounded-2xl text-center">
                    <p className="text-3xl font-serif text-primary">{stats.totalEntries}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Entries</p>
                  </Card>
                  <Card className="p-4 rounded-2xl text-center">
                    <p className="text-3xl font-serif text-blue-600">{stats.avgEnergy || '—'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Avg Energy</p>
                  </Card>
                  <Card className="p-4 rounded-2xl text-center">
                    <p className="text-3xl font-serif text-emerald-600">{stats.comfortablePercent !== null ? `${stats.comfortablePercent}%` : '—'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Comfortable Days</p>
                  </Card>
                  <Card className="p-4 rounded-2xl text-center">
                    <p className="text-lg font-serif">{stats.topMood || '—'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Most Frequent</p>
                  </Card>
                </div>

                {/* Energy Over Time Chart */}
                {energyTrendData.length > 0 && (
                  <Card className="p-6 rounded-2xl" data-testid="energy-chart">
                    <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                      <Battery size={18} className="text-blue-600" />
                      Energy Level Over Time
                    </h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={energyTrendData}>
                          <XAxis 
                            dataKey="date" 
                            tick={CHART_TICK_STYLE}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis 
                            domain={[0, 10]} 
                            tick={CHART_TICK_STYLE}
                            tickLine={false}
                            axisLine={false}
                            width={30}
                          />
                          <Tooltip 
                            contentStyle={CHART_TOOLTIP_STYLE}
                            formatter={(value) => [`${value}/10`, 'Energy']}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="energy" 
                            stroke="#2563eb" 
                            strokeWidth={2}
                            dot={ENERGY_DOT_STYLE}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {/* Mood Frequency Chart */}
                {moodFrequency.length > 0 && (
                  <Card className="p-6 rounded-2xl" data-testid="mood-frequency-chart">
                    <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                      <span className="text-lg">🎭</span>
                      Most Common Emotions
                    </h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={moodFrequency} layout="vertical">
                          <XAxis type="number" hide />
                          <YAxis 
                            dataKey="mood" 
                            type="category" 
                            tick={CHART_TICK_STYLE}
                            tickLine={false}
                            axisLine={false}
                            width={100}
                          />
                          <Tooltip 
                            contentStyle={CHART_TOOLTIP_STYLE}
                            formatter={(value) => [`${value} entries`, 'Count']}
                          />
                          <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                            {moodFrequency.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        <span>Comfortable</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span>Uncomfortable</span>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Mood Timeline */}
                {moodTrendData.length > 0 && (
                  <Card className="p-6 rounded-2xl" data-testid="mood-timeline">
                    <h3 className="font-serif text-lg mb-4">Recent Mood Timeline</h3>
                    <div className="flex flex-wrap gap-2">
                      {moodTrendData.map((entry) => (
                        <div 
                          key={entry.date}
                          className={`px-3 py-2 rounded-xl text-xs ${
                            entry.moodType === 'comfortable'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          <div className="font-medium">{entry.mood}</div>
                          <div className="opacity-70">{entry.date}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
